<script lang="ts">
  import { createQuery } from '@tanstack/svelte-query';
  import { session } from '$lib/state/session.svelte';
  import { route } from '$lib/state/route.svelte';
  import { getErrorMessage } from '$lib/api/client';
  import { roleProfile, workflowAccessKey } from '$lib/utils/roleProfile';
  import { canUseHappyQueue } from '$lib/utils/happyCode';
  import { callQueries, callItems, callPrompts, type CallKind } from './coordinatorQueries';

  // One customer at a time. Outcomes are recorded in the existing queue,
  // case or contact workflow (review links, same-day cases, retry dates),
  // never re-implemented here.
  const profile = $derived(roleProfile(session.me));
  const happyAccess = $derived(canUseHappyQueue(session.me?.user, session.me?.happy_code_queue_access ?? false, session.me?.verticals));
  const queries = $derived(callQueries({ profile, userId: session.me?.user.id, vertical: session.selectedVertical, accessKey: workflowAccessKey(session.me), happyAccess }));
  const happy = createQuery(() => queries.happy);
  const fixes = createQuery(() => queries.fixes);
  const callbacks = createQuery(() => queries.callbacks);
  let filter = $state<'all' | CallKind>('all');
  const all = $derived(callItems(happy.data, fixes.data?.need_to_speak?.results ?? [], callbacks.data?.contact_calls?.results ?? []));
  const shown = $derived(filter === 'all' ? all : all.filter(item => item.kind === filter || (filter === 'day7' && item.kind === 'negative')));
  const current = $derived(shown[0] ?? null);
  const pending = $derived(fixes.isPending || callbacks.isPending || (happyAccess && happy.isPending));
  const errors = $derived([happyAccess && happy.isError ? happy.error : null, fixes.isError ? fixes.error : null, callbacks.isError ? callbacks.error : null].filter(Boolean));
  const counts = $derived({
    day7: (happy.data?.due_calls?.length ?? 0) + (happy.data?.negatives?.length ?? 0),
    fix: fixes.data?.need_to_speak?.count ?? 0,
    callback: callbacks.data?.contact_calls?.count ?? 0
  });
  const dot: Record<CallKind, string> = { negative: 'bg-[#b23a26]', day7: 'bg-toyama-lime', fix: 'bg-toyama-teal', callback: 'bg-[#d08700]' };
  function retry() { void happy.refetch(); void fixes.refetch(); void callbacks.refetch(); }
</script>

{#if profile === 'coordination' || profile === 'care'}
  <section class="day-card flex flex-col gap-4" aria-labelledby="call-queue">
    <div class="flex flex-wrap items-center justify-between gap-2">
      <h2 id="call-queue" class="day-title">Call queue</h2>
      <div class="day-segment" role="group" aria-label="Call type">
        <button aria-pressed={filter === 'all'} onclick={() => filter = 'all'}>All · {all.length}</button>
        {#if happyAccess}<button aria-pressed={filter === 'day7'} onclick={() => filter = 'day7'}>Happy calls · {counts.day7}</button>{/if}
        <button aria-pressed={filter === 'fix'} onclick={() => filter = 'fix'}>Fix confirmations · {counts.fix}</button>
        <button aria-pressed={filter === 'callback'} onclick={() => filter = 'callback'}>Callbacks · {counts.callback}</button>
      </div>
    </div>
    {#each errors as error}<p role="alert" class="text-sm text-red-700">{getErrorMessage(error)} <button class="underline" onclick={retry}>Retry</button></p>{/each}
    {#if pending && !all.length}<p role="status" class="day-meta">Loading your calls…</p>
    {:else if current}
      <div class="day-card-dark flex flex-col gap-3.5">
        <div class="flex flex-wrap items-start justify-between gap-3">
          <div class="min-w-0">
            <p class="text-xs font-semibold uppercase tracking-[0.08em] {current.kind === 'negative' ? 'text-[#ffb4a6]' : 'text-toyama-lime'}">{current.label}</p>
            <h3 class="mt-1 font-heading text-2xl font-bold">{current.name}</h3>
            {#if current.context}<p class="mt-1 text-sm text-[#c9d3de]">{current.context}</p>{/if}
          </div>
          {#if current.phone}
            <a href={`tel:${current.phone.replace(/[^\d+]/g, '')}`} class="inline-flex min-h-[44px] items-center gap-2 rounded-xl bg-toyama-lime px-4 py-2.5 font-semibold text-navy">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.4 1.8.7 2.7a2 2 0 0 1-.5 2.1L8 9.8a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.7.7a2 2 0 0 1 1.7 2z"></path></svg>
              {current.phone}
            </a>
          {:else}<span class="text-sm text-[#c9d3de]">No phone on record</span>{/if}
        </div>
        <p class="rounded-xl bg-[#1a3252] px-3.5 py-3 text-[13px] text-[#e3e8ee]"><strong class="text-white">Ask:</strong> {callPrompts[current.kind]}</p>
        <div class="flex flex-wrap gap-2">
          <button class="inline-flex min-h-[44px] items-center rounded-xl bg-white px-4 font-semibold text-navy" onclick={() => route.push(current.target)}>Record outcome →</button>
          <span class="self-center text-xs text-[#c9d3de]">{current.kind === 'day7' || current.kind === 'negative' ? 'Happy → review link · Not happy → same-day case · No answer → retry date' : current.kind === 'fix' ? 'Reached or no answer is logged on the case' : 'Log the call on the contact'}</span>
        </div>
      </div>
      {#if shown.length > 1}
        <div>
          <h3 class="mb-1 text-[13px] font-semibold text-dark-gray">Up next</h3>
          {#each shown.slice(1, 6) as item (item.key)}
            <div class="day-row">
              <span class="h-2.5 w-2.5 shrink-0 rounded-full {dot[item.kind]}" aria-hidden="true"></span>
              <div class="min-w-0 flex-1"><button class="truncate text-left font-semibold text-navy hover:underline" onclick={() => route.push(item.target)}>{item.name}</button><p class="truncate text-xs text-dark-gray">{item.label}{#if item.context} · {item.context}{/if}</p></div>
            </div>
          {/each}
          {#if shown.length > 6}<p class="mt-2 text-xs text-dark-gray">+{shown.length - 6} more in this view.</p>{/if}
        </div>
      {/if}
    {:else if !errors.length}
      <div class="rounded-2xl bg-[#ecf8cc] p-7 text-center text-[#2e4400]"><p class="font-heading text-xl font-bold">Call queue cleared.</p><p class="mt-1 text-sm">Nobody is waiting for a call in this view.</p></div>
    {/if}
    <div class="flex flex-wrap gap-4">
      {#if happyAccess}<button class="day-link" onclick={() => route.push({ tab: 'happy-code-queue' })}>Open the full call queue →</button>{/if}
    </div>
  </section>
{/if}
